class CollectionProductsSlider {
  constructor() {
    this.sections = new Map();
    this.resizeTimer = null;
    this.mediaQuery = window.matchMedia('(max-width: 749px)');

    this.init();
  }

  init() {
    // Use a single event listener for all sections
    document.addEventListener('DOMContentLoaded', () => {
      this.initializeSections();
      this.setupGlobalListeners();
    });
  }

  initializeSections() {
    const sections = document.querySelectorAll('.looking-for__section');
    sections.forEach((section) => this.initializeSection(section));
  }

  initializeSection(sectionElement) {
    const sectionId = sectionElement.closest('[id^="shopify-section-"]').id;
    const bubblesWrapper = sectionElement.querySelector('.bubbles--wrapper');
    const bubbles = sectionElement.querySelectorAll('.bubbles--item');
    const productWrappers = sectionElement.querySelectorAll('.products--wrapper');

    if (!sectionId || this.sections.has(sectionId)) {
      return; // Already initialized or invalid section
    }

    const sectionData = {
      element: sectionElement,
      bubblesWrapper,
      bubbles,
      productWrappers,
      currentIndex: 0,
      productFlkty: [],
      bubbleFlkty: null,
    };

    // Initialize bubble Flickity if needed
    if (bubbles.length > 6 && bubblesWrapper) {
      sectionData.bubbleFlkty = new Flickity(bubblesWrapper, {
        pageDots: false,
        prevNextButtons: true,
        cellAlign: 'left',
        contain: true,
        draggable: true,
        groupCells: true,
      });
    }

    // Set up bubble click handlers using event delegation
    this.setupBubbleClicks(sectionElement, sectionData);

    // Initialize product sliders based on screen size
    this.handleProductSliders(sectionData);

    this.sections.set(sectionId, sectionData);
  }

  setupBubbleClicks(sectionElement, sectionData) {
    // Use event delegation for better performance
    sectionElement.addEventListener('click', (e) => {
      const bubble = e.target.closest('.bubbles--item');
      if (!bubble) return;

      const index = parseInt(bubble.dataset.index);
      if (isNaN(index)) return;

      // Update active bubble
      sectionData.bubbles.forEach((b) => b.classList.remove('is-active'));
      bubble.classList.add('is-active');

      // Show corresponding products
      sectionData.productWrappers.forEach((wrapper) => wrapper.classList.add('hidden'));
      if (sectionData.productWrappers[index]) {
        sectionData.productWrappers[index].classList.remove('hidden');
      }

      // Refresh Flickity if on mobile
      if (this.mediaQuery.matches && sectionData.productFlkty[index]) {
        this.reflow(sectionData.productFlkty[index], sectionData.productWrappers[index]);
      }

      sectionData.currentIndex = index;
    });
  }

  handleProductSliders(sectionData) {
    if (!sectionData) return;

    const isMobile = this.mediaQuery.matches;

    sectionData.productWrappers.forEach((wrapper, index) => {
      if (isMobile && !sectionData.productFlkty[index]) {
        // Initialize Flickity on mobile
        sectionData.productFlkty[index] = new Flickity(wrapper, {
          cellAlign: 'left',
          pageDots: true,
          prevNextButtons: false,
          wrapAround: false,
          draggable: true,
          groupCells: true,
        });
        // Recompute height now, and again once fonts/reviews/images settle
        this.reflow(sectionData.productFlkty[index], wrapper);
        window.addEventListener('load', () => this.reflow(sectionData.productFlkty[index], wrapper), {
          once: true,
        });
      } else if (!isMobile && sectionData.productFlkty[index]) {
        // Destroy Flickity on desktop
        sectionData.productFlkty[index].destroy();
        sectionData.productFlkty[index] = null;
      }
    });
  }

  handleAllProductSliders() {
    this.sections.forEach((sectionData) => {
      this.handleProductSliders(sectionData);
    });
  }

  // Flickity fixes the viewport height once at init. If content grows afterwards
  // (font swap, async reviews badge, images) the cards stay too short and the
  // price gets clipped by the viewport's overflow:hidden. Unpin the card heights,
  // let Flickity re-measure the tallest natural card, then hand height back to
  // the CSS (height:100%) so all cards match the tallest without clipping.
  reflow(flkty, wrapper) {
    if (!flkty || !wrapper) return;
    const cards = wrapper.querySelectorAll('.product-card-wrapper');
    cards.forEach((c) => (c.style.height = 'auto'));
    flkty.resize();
    cards.forEach((c) => c.style.removeProperty('height'));
  }

  setupGlobalListeners() {
    // Single media query listener for all sections
    this.mediaQuery.addListener(() => this.handleAllProductSliders());

    // Single resize handler for all sections
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.handleAllProductSliders();
      }, 250);
    });
  }

  // Public method to manually initialize a section (useful for dynamically added content)
  initializeNewSection(sectionElement) {
    this.initializeSection(sectionElement);
  }

  // Cleanup method for removing sections
  removeSection(sectionId) {
    const sectionData = this.sections.get(sectionId);
    if (!sectionData) return;

    // Cleanup Flickity instances
    if (sectionData.bubbleFlkty) {
      sectionData.bubbleFlkty.destroy();
    }
    sectionData.productFlkty.forEach((flkty) => {
      if (flkty) flkty.destroy();
    });

    this.sections.delete(sectionId);
  }
}

// Initialize the global instance
if (typeof window.collectionProductsSlider === 'undefined') {
  window.collectionProductsSlider = new CollectionProductsSlider();
}

// Expose for potential external use
window.CollectionProductsSlider = CollectionProductsSlider;
